import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { BaseModal } from '@/components/shared';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/contexts/AuthContext';
import {
  aplicarImportacaoGlpi,
  configSIService,
  previaImportacaoGlpi,
  type ConfigUsuarioGlpi,
  type ItemPreviaImportacaoGlpi,
  type SignificadoUsuarioGlpi,
  type UsuarioGlpiNaoEncontrado,
} from '../../../../services';
import UsuariosGlpiPainel from './UsuariosGlpiPainel';
import type { ContextoAtivos } from './ativos.utils';
import ImportarGlpiLinha, {
  ACAO_LABELS,
  FILTROS_PENDENCIA,
  atendeFiltroPendencia,
  podeImportar,
  type DecisaoLocal,
  type FiltroPendencia,
} from './ImportarGlpiLinha';
import AjustarItemImportacaoModal from './AjustarItemImportacaoModal';

type Acao = ItemPreviaImportacaoGlpi['acao'];
const ORDEM_ACOES: Acao[] = ['novo', 'completar', 'conflito', 'sem_alteracao', 'excluido'];

interface ImportarGlpiModalProps {
  isOpen: boolean;
  onClose: () => void;
  contexto: ContextoAtivos;
}

/**
 * "Importar do GLPI" (fase 4): busca a prévia (nada é gravado), mostra o que seria feito —
 * novo / completar (só campos vazios) / conflito / sem alteração — e grava só o que o SI
 * deixar marcado. Pode rodar de novo quantas vezes quiser: o que já foi importado aparece
 * como "sem alteração". Avisos (status sem situação, usuário não encontrado...) viram
 * pendência na ficha pra resolver depois.
 */
export default function ImportarGlpiModal({ isOpen, onClose, contexto }: ImportarGlpiModalProps) {
  const toast = useToast();
  const [itens, setItens] = useState<ItemPreviaImportacaoGlpi[] | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set());
  const [filtroAcao, setFiltroAcao] = useState<Acao | 'todos'>('todos');
  const [busca, setBusca] = useState('');
  const [filtroPendencia, setFiltroPendencia] = useState<FiltroPendencia>('todas');
  // Ajustes, divergências escolhidas e resoluções de conflito feitos na tela, por item.
  const [decisoes, setDecisoes] = useState<Map<string, DecisaoLocal>>(new Map());
  const [ajustando, setAjustando] = useState<ItemPreviaImportacaoGlpi | null>(null);
  const { user } = useAuth();
  const [usuarios, setUsuarios] = useState<UsuarioGlpiNaoEncontrado[]>([]);
  const [configsUsuarios, setConfigsUsuarios] = useState<Map<string, ConfigUsuarioGlpi | null>>(new Map());

  /** Ajustes que um usuário-marcador provoca nos ativos dele (sem colaborador; Situação/Cliente
   * do significado). O servidor aplica a mesma config ao importar — aqui é pra tela refletir já. */
  const ajustesDoMarcador = (significado: SignificadoUsuarioGlpi | null, item: ItemPreviaImportacaoGlpi): Record<string, string | null> => {
    // Sem significado: fica o status do GLPI — e o "não localizado" por falta de colaborador
    // deixa de valer (não é uma pessoa, então não falta ninguém).
    if (!significado?.statusCustodia) {
      return item.naoLocalizadoSemColaborador
        ? { colaboradorAtualId: null, statusCustodia: 'com_colaborador', situacaoDetalhe: null }
        : { colaboradorAtualId: null };
    }
    // Mesma situação do status e sem detalhe (ex.: CASA RARO = Em estoque): fica o detalhe do
    // status de cada máquina (Disponível, Com pendência...) — mesma regra do servidor.
    const mesmaSituacaoSemDetalhe = !significado.situacaoDetalhe && significado.statusCustodia === item.statusCustodia;
    return {
      colaboradorAtualId: null,
      clienteAtualId: significado.clienteId ?? null,
      fornecedorManutencao: significado.statusCustodia === 'em_manutencao' ? (significado.fornecedorManutencao ?? null) : null,
      ...(mesmaSituacaoSemDetalhe ? {} : { statusCustodia: significado.statusCustodia, situacaoDetalhe: significado.situacaoDetalhe ?? null }),
    };
  };
  const CHAVES_MARCADOR = ['colaboradorAtualId', 'statusCustodia', 'situacaoDetalhe', 'clienteAtualId', 'fornecedorManutencao'];

  const aplicarAosItensDoUsuario = (
    login: string,
    significado: SignificadoUsuarioGlpi | null | 'desfazer' | { colaboradorId: string }
  ) =>
    setDecisoes((atual) => {
      const novo = new Map(atual);
      for (const item of itens ?? []) {
        if (item.usuarioLogin !== login) continue;
        const decisao = novo.get(item.chave) ?? {};
        const semMarcador = Object.fromEntries(Object.entries(decisao.ajustes ?? {}).filter(([k]) => !CHAVES_MARCADOR.includes(k)));
        const ajustes =
          significado === 'desfazer'
            ? {}
            : significado && 'colaboradorId' in significado
              ? {
                  colaboradorAtualId: significado.colaboradorId,
                  // Era "não localizado" só por não achar a pessoa → volta pra Em uso.
                  ...(item.naoLocalizadoSemColaborador ? { statusCustodia: 'com_colaborador', situacaoDetalhe: null } : {}),
                }
              : ajustesDoMarcador(significado, item);
        novo.set(item.chave, { ...decisao, ajustes: { ...semMarcador, ...ajustes } });
      }
      return novo;
    });

  const ator = user ? { id: user.collaboratorId ?? user.uid, name: user.displayName ?? user.email ?? '' } : null;

  const salvarUsuario = async (u: UsuarioGlpiNaoEncontrado, significado: SignificadoUsuarioGlpi | null) => {
    if (!ator) return;
    try {
      await configSIService.setUsuarioGlpi(
        u.login,
        {
          statusCustodia: significado?.statusCustodia ?? null,
          situacaoDetalhe: significado?.situacaoDetalhe ?? null,
          clienteId: significado?.clienteId ?? null,
          fornecedorManutencao: significado?.statusCustodia === 'em_manutencao' ? (significado.fornecedorManutencao ?? null) : null,
          nome: u.nome,
        },
        ator
      );
      setConfigsUsuarios((atual) => new Map(atual).set(u.login, { ...significado, naoEPessoa: true }));
      aplicarAosItensDoUsuario(u.login, significado);
      toast.success(`"${u.nome}" marcado como não sendo uma pessoa — vale pra esta e as próximas importações.`);
    } catch (error: any) {
      toast.error(`Não foi possível salvar: ${error?.message || 'erro desconhecido'}`);
    }
  };

  const vincularColaborador = async (u: UsuarioGlpiNaoEncontrado, colaborador: { id: string; name: string }) => {
    if (!ator) return;
    try {
      await configSIService.setUsuarioGlpiColaborador(
        u.login,
        { colaboradorId: colaborador.id, colaboradorNome: colaborador.name, nome: u.nome },
        ator
      );
      setConfigsUsuarios((atual) =>
        new Map(atual).set(u.login, { naoEPessoa: false, colaboradorId: colaborador.id, colaboradorNome: colaborador.name })
      );
      aplicarAosItensDoUsuario(u.login, { colaboradorId: colaborador.id });
      toast.success(`"${u.nome}" vinculado a ${colaborador.name} — vale pra esta e as próximas importações.`);
    } catch (error: any) {
      toast.error(`Não foi possível salvar: ${error?.message || 'erro desconhecido'}`);
    }
  };

  const desfazerUsuario = async (u: UsuarioGlpiNaoEncontrado) => {
    if (!ator) return;
    try {
      await configSIService.setUsuarioGlpi(u.login, null, ator);
      const jaVinhaDaPrevia = Boolean(u.config?.naoEPessoa || u.config?.colaboradorId);
      setConfigsUsuarios((atual) => new Map(atual).set(u.login, null));
      aplicarAosItensDoUsuario(u.login, 'desfazer');
      if (jaVinhaDaPrevia) toast.info('Desfeito. Leia o GLPI de novo pra ver os ativos desse usuário sem a marcação.');
    } catch (error: any) {
      toast.error(`Não foi possível desfazer: ${error?.message || 'erro desconhecido'}`);
    }
  };

  const definirDecisao = (chave: string, decisao: DecisaoLocal) => {
    setDecisoes((atual) => new Map(atual).set(chave, decisao));
    // Resolveu o conflito ou ajustou → já marca pra importar.
    setMarcadas((atual) => new Set(atual).add(chave));
  };

  const buscarPrevia = async () => {
    setCarregando(true);
    try {
      const previa = await previaImportacaoGlpi();
      const resultado = previa.itens;
      setItens(resultado);
      setUsuarios(previa.usuariosNaoEncontrados);
      setConfigsUsuarios(new Map(previa.usuariosNaoEncontrados.map((u) => [u.login, u.config])));
      setDecisoes(new Map());
      // Padrão: tudo que pode ser importado já vem marcado.
      setMarcadas(new Set(resultado.filter((i) => i.acao === 'novo' || i.acao === 'completar').map((i) => i.chave)));
    } catch (error: any) {
      toast.error(`Não foi possível ler o GLPI: ${error?.message || 'erro desconhecido'}`);
    } finally {
      setCarregando(false);
    }
  };

  const contagem = useMemo(() => {
    const c = new Map<Acao, number>();
    (itens ?? []).forEach((i) => c.set(i.acao, (c.get(i.acao) ?? 0) + 1));
    return c;
  }, [itens]);

  const exibidos = useMemo(() => {
    const alvo = busca.trim().toLowerCase();
    return (itens ?? [])
      .filter((i) => filtroAcao === 'todos' || i.acao === filtroAcao)
      .filter((i) => atendeFiltroPendencia(i, decisoes.get(i.chave), filtroPendencia))
      .filter((i) => !alvo || [i.patrimonio, i.nSerie, i.modelo, i.usuarioGlpi].some((v) => v?.toLowerCase().includes(alvo)))
      .sort((a, b) => ORDEM_ACOES.indexOf(a.acao) - ORDEM_ACOES.indexOf(b.acao) || (a.patrimonio ?? '').localeCompare(b.patrimonio ?? '', 'pt-BR', { numeric: true }));
  }, [itens, filtroAcao, filtroPendencia, decisoes, busca]);

  // Quantos em cada filtro de pendência — respeita o filtro de ação escolhido, e os ajustes.
  const contagemPendencia = useMemo(() => {
    const base = (itens ?? []).filter((i) => filtroAcao === 'todos' || i.acao === filtroAcao);
    return new Map(FILTROS_PENDENCIA.map((f) => [f.valor, base.filter((i) => atendeFiltroPendencia(i, decisoes.get(i.chave), f.valor)).length]));
  }, [itens, filtroAcao, decisoes]);

  const alternar = (chave: string) =>
    setMarcadas((atual) => {
      const novo = new Set(atual);
      if (novo.has(chave)) novo.delete(chave);
      else novo.add(chave);
      return novo;
    });

  // Marcar/desmarcar valem pro que está na tela (filtro + busca) — dá pra importar aos poucos
  // (ex.: filtrar "RLMP" e importar só esses); o resto continua como "Novo" na próxima vez.
  const selecionaveisNaTela = exibidos.filter((i) => podeImportar(i, decisoes.get(i.chave)));
  const marcadasValidas = (itens ?? []).filter((i) => marcadas.has(i.chave) && podeImportar(i, decisoes.get(i.chave)));
  const marcarNaTela = (marcar: boolean) =>
    setMarcadas((atual) => {
      const novo = new Set(atual);
      selecionaveisNaTela.forEach((i) => (marcar ? novo.add(i.chave) : novo.delete(i.chave)));
      return novo;
    });

  const aplicar = async () => {
    if (marcadasValidas.length === 0) return;
    if (!window.confirm(`Importar ${marcadasValidas.length} ${marcadasValidas.length === 1 ? 'item' : 'itens'} do GLPI?`)) return;
    setAplicando(true);
    try {
      const r = await aplicarImportacaoGlpi(marcadasValidas.map((i) => ({ chave: i.chave, ...decisoes.get(i.chave) })));
      toast.success(`Importação concluída: ${r.criados} novos, ${r.completados} completados.`);
      if (r.ignorados.length > 0) {
        toast.info(`${r.ignorados.length} não importado(s): ${r.ignorados.slice(0, 3).map((x) => x.motivo).join('; ')}${r.ignorados.length > 3 ? '…' : ''}`);
      }
      setItens(null);
      onClose();
    } catch (error: any) {
      toast.error(`Não foi possível importar: ${error?.message || 'erro desconhecido'}`);
    } finally {
      setAplicando(false);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Importar do GLPI"
      maxWidth="max-w-5xl"
      actions={
        <div className="flex items-center justify-end gap-3">
          {itens && <span className="mr-auto text-sm text-gray-500">{marcadasValidas.length} marcados pra importar</span>}
          <button type="button" className="btn-secondary" onClick={onClose}>
            Fechar
          </button>
          {itens && (
            <button type="button" className="btn-primary" disabled={marcadasValidas.length === 0 || aplicando} onClick={aplicar}>
              {aplicando ? 'Importando...' : `Importar ${marcadasValidas.length}`}
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-4 py-4">
        {!itens && (
          <div className="space-y-3 text-sm text-gray-600">
            <p>
              Lê o inventário do GLPI (computadores, monitores, telefones, chips, impressoras e cartões de acesso) e mostra o que seria
              feito. <strong>Nada é gravado</strong> até você confirmar. Cadastros que já existem no Digital só têm campos vazios
              completados — nada é sobrescrito, e a Situação não muda.
            </p>
            <button type="button" className="btn-primary flex items-center gap-2" disabled={carregando} onClick={buscarPrevia}>
              <ArrowPathIcon className={clsx('h-4 w-4', carregando && 'animate-spin')} />
              {carregando ? 'Lendo o GLPI (pode levar 1–2 min)...' : 'Ler o GLPI e montar a prévia'}
            </button>
          </div>
        )}

        {itens && (
          <>
            <UsuariosGlpiPainel
              usuarios={usuarios}
              configs={configsUsuarios}
              onSalvar={salvarUsuario}
              onVincularColaborador={vincularColaborador}
              onDesfazer={desfazerUsuario}
              contexto={contexto}
            />
            <div className="flex flex-wrap items-center gap-2">
              {(['todos', ...ORDEM_ACOES] as const).map((acao) => (
                <button
                  key={acao}
                  type="button"
                  onClick={() => setFiltroAcao(acao)}
                  className={clsx(
                    'rounded-md border px-2.5 py-1 text-sm',
                    filtroAcao === acao ? 'border-blue-600 bg-blue-50 font-medium text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  )}
                >
                  {acao === 'todos' ? `Todos ${itens.length}` : `${ACAO_LABELS[acao]} ${contagem.get(acao) ?? 0}`}
                </button>
              ))}
              <input
                className="input-primary ml-auto w-64"
                placeholder="Buscar patrimônio, série, usuário..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Pendência</span>
              {FILTROS_PENDENCIA.map((f) => (
                <button
                  key={f.valor}
                  type="button"
                  onClick={() => setFiltroPendencia(f.valor)}
                  className={clsx(
                    'rounded-full border px-2.5 py-0.5 text-xs',
                    filtroPendencia === f.valor ? 'border-amber-500 bg-amber-50 font-medium text-amber-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  )}
                >
                  {f.label} {contagemPendencia.get(f.valor) ?? 0}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3 text-xs">
              <button type="button" className="font-medium text-blue-600 hover:underline disabled:opacity-40" disabled={selecionaveisNaTela.length === 0} onClick={() => marcarNaTela(true)}>
                Marcar todos {filtroAcao !== 'todos' || filtroPendencia !== 'todas' || busca ? 'desta lista' : ''} ({selecionaveisNaTela.length})
              </button>
              <button type="button" className="font-medium text-blue-600 hover:underline disabled:opacity-40" disabled={selecionaveisNaTela.length === 0} onClick={() => marcarNaTela(false)}>
                Desmarcar todos {filtroAcao !== 'todos' || filtroPendencia !== 'todas' || busca ? 'desta lista' : ''}
              </button>
            </div>
            <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
              {exibidos.map((item) => (
                <ImportarGlpiLinha
                  key={item.chave}
                  item={item}
                  decisao={decisoes.get(item.chave)}
                  onDecisao={(decisao) => definirDecisao(item.chave, decisao)}
                  marcado={marcadas.has(item.chave)}
                  onAlternar={() => alternar(item.chave)}
                  onAjustar={() => setAjustando(item)}
                  contexto={contexto}
                />
              ))}
              {exibidos.length === 0 && <li className="p-4 text-center text-sm text-gray-400">Nada aqui.</li>}
            </ul>
          </>
        )}
      </div>

      <AjustarItemImportacaoModal
        item={ajustando}
        decisao={ajustando ? decisoes.get(ajustando.chave) : undefined}
        onClose={() => setAjustando(null)}
        onSalvar={(ajustes) => {
          if (ajustando) definirDecisao(ajustando.chave, { ...decisoes.get(ajustando.chave), ajustes });
          setAjustando(null);
        }}
      />
    </BaseModal>
  );
}
